import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Checkbox,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { getActiveOrganisation, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchBatchConfig,
  fetchBillingUsage,
  fetchSubscription,
} from "data/queries";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

import { LoadingButton } from "@mui/lab";
import { quoteBatchJob, startBatchJob } from "data/mutations";
import {
  BatchConfigProps,
  StepperControlProps,
} from "pages/geoscape-batch/types";
import { Md5 } from "ts-md5";
import { formatTimestampBatchJob } from "utils/format-date";

interface StartBatchJobProps {}

export const StartBatchJob = (
  props: StartBatchJobProps & BatchConfigProps & StepperControlProps
) => {
  const [identityState] = useIdentity();
  const isUser = !getActiveOrganisation(identityState);
  const identityId = resolveIdentityId(identityState, isUser);
  const history = useHistory();

  const subscriptionQuery = useQuery([QueryKeys.subscription, identityId], () =>
    fetchSubscription(isUser, identityState)
  );

  const isEnterprise: boolean =
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data.activeSubscription.plan.isEnterprise;

  const queryClient = useQueryClient();

  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const [quotedBatchConfigAttempts, setQuotedBatchConfigAttempts] = useState(0);

  // Calculate a hash for the batch job
  const batchConfigHash = Md5.hashStr(
    JSON.stringify(props.batchConfig.input) +
      JSON.stringify(props.batchConfig.output)
  );

  const quoteJobMutation = useMutation(
    [QueryKeys.batchesQuoteJob, identityId],
    () => quoteBatchJob(props.batchConfig.batchInfo.batchId, identityState),
    {
      onSuccess: () => {
        queryClient.removeQueries([
          QueryKeys.batchesQuotedBatchConfig,
          identityId,
        ]);
      },
    }
  );

  if (quotedBatchConfigAttempts % 5 == 0) {
    quoteJobMutation.mutate();
    setQuotedBatchConfigAttempts((prev) => prev + 1);
  }

  // Fetch the batch config once it has been quoted
  const quotedBatchConfigQuery = useQuery(
    [QueryKeys.batchesQuotedBatchConfig, identityId],
    () => fetchBatchConfig(props.batchConfig.batchInfo.batchId, identityState),
    {
      onSuccess: (data) => {
        setQuotedBatchConfigAttempts((prev) => prev + 1);

        if (
          data?.status === "Quoted" &&
          props.batchConfig.status !== "Quoted"
        ) {
          props.setBatchConfig(data);
        }
      },
      refetchInterval: (data, query) => {
        const refetchTime = 1000;
        if (data?.status != "Quoted") return refetchTime;

        return false;
      },
    }
  );

  const billingUsageQuery = useQuery([QueryKeys.billingUsage, identityId], () =>
    fetchBillingUsage(isUser, identityState)
  );

  const startJobMutation = useMutation(
    [QueryKeys.batchesStartJob, identityId],
    () => startBatchJob(props.batchConfig.batchInfo.batchId, identityState),
    {
      onSuccess: () => {
        queryClient.removeQueries([
          QueryKeys.batchesQuotedBatchConfig,
          identityId,
        ]);

        history.push(Routes.batchBatches);
      },
    }
  );

  // Update the batch config hash if it has changed
  if (batchConfigHash !== props.batchConfig.hubInfo.batchConfigHash) {
    props.setBatchConfig((prev) => ({
      ...prev,
      hubInfo: {
        ...prev.hubInfo,
        batchConfigHash: batchConfigHash,
      },
    }));
  }

  // Only run the quote mutation if the batch config hash has changed
  useEffect(() => {
    quoteJobMutation.mutate();
  }, [props.batchConfig.hubInfo.batchConfigHash]);

  const isLoading =
    quoteJobMutation.isLoading ||
    billingUsageQuery.isLoading ||
    quotedBatchConfigQuery.isLoading ||
    quotedBatchConfigQuery.data?.status !== "Quoted";

  // We include !isLoading here because even though quotedBatchConfig may finish, we need to refetch until the job has been quoted
  const isSuccess =
    quoteJobMutation.isSuccess &&
    quotedBatchConfigQuery.isSuccess &&
    billingUsageQuery.isSuccess &&
    quotedBatchConfigQuery.data?.status === "Quoted";

  const creditBalance = isSuccess
    ? billingUsageQuery.data.allowance +
      billingUsageQuery.data.overageCredits -
      billingUsageQuery.data.creditsUsedTotal
    : 0;

  const remainingBalance = isSuccess
    ? creditBalance - quotedBatchConfigQuery.data.batchInfo.estimatedCreditCost
    : 0;

  const hasCredits = remainingBalance > 0;

  return (
    <Card
      sx={{
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
    >
      <Box>
        <Typography variant="h5">Confirm and Start</Typography>
      </Box>

      {isLoading && <CustomSkeleton />}

      {isSuccess && (
        <>
          <Box sx={{ display: "grid", gridAutoColumns: "1fr", gap: "16px" }}>
            <Card
              sx={{
                gridRow: "1",
                gridColumn: "1",
                padding: "16px",
                boxShadow: 0,
                border: "1px solid lightgrey",
              }}
            >
              <Typography variant="h5" sx={{ marginBottom: "16px" }}>
                Batch Job Information
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Total Rows</TableCell>
                      <TableCell>
                        {quotedBatchConfigQuery.data.batchInfo.totalRows.toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Estimated Time To Finish</TableCell>
                      <TableCell>
                        {formatTimestampBatchJob(
                          quotedBatchConfigQuery.data.batchInfo.estimatedRunTime
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Credit Balance</TableCell>
                      <TableCell>{creditBalance.toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Estimated Credit Cost</TableCell>
                      <TableCell>
                        {quotedBatchConfigQuery.data.batchInfo.estimatedCreditCost.toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Remaining Balance</TableCell>
                      <TableCell>{remainingBalance.toLocaleString()}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            <Card
              sx={{
                gridRow: "1",
                gridColumn: "span 2",
                padding: "16px",
                boxShadow: 0,
                border: "1px solid lightgrey",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h5">Confirm & Start Job</Typography>
                <Typography>
                  When you are happy with the job configuration, please click
                  the checkbox and confirm. The estimated credit cost will be
                  deducted from your credit balance. Upon successful completion
                  of the job, any unused credits will be refunded. No credits
                  are charged for failed batch jobs.
                </Typography>
              </Box>

              {(hasCredits || isEnterprise) && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                  }}
                >
                  <Typography>Confirm</Typography>
                  <Checkbox
                    checked={isConfirmed}
                    onClick={() => setIsConfirmed(!isConfirmed)}
                  />
                </Box>
              )}

              {!hasCredits && !isEnterprise && (
                <Alert severity="error">
                  <AlertTitle>Insufficient Credits</AlertTitle>
                  You are unable to start the batch job due to insufficient
                  credits. Please add more credits and try again.
                </Alert>
              )}
            </Card>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              gap: "16px",
            }}
          >
            <Button
              onClick={() =>
                props.setActiveStep(Math.max(props.activeStep - 1, 0))
              }
            >
              Back
            </Button>
            <LoadingButton
              loading={startJobMutation.isLoading}
              onClick={() => startJobMutation.mutate()}
              disabled={!isConfirmed}
              variant="contained"
            >
              Start
            </LoadingButton>
          </Box>
        </>
      )}
    </Card>
  );
};

const CustomSkeleton = () => {
  return (
    <>
      <Box sx={{ display: "grid", gridAutoColumns: "1fr", gap: "16px" }}>
        <Skeleton
          variant="rectangular"
          height={247}
          sx={{ gridRow: "1", gridColumn: "1" }}
        />
        <Skeleton
          variant="rectangular"
          height={247}
          sx={{ gridRow: "1", gridColumn: "span 2" }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: "16px",
        }}
      >
        <Skeleton
          variant="rectangular"
          width={64}
          height={36}
          sx={{ gridRow: "1", gridColumn: "span 2" }}
        />
        <Skeleton
          variant="rectangular"
          width={78}
          height={36}
          sx={{ gridRow: "1", gridColumn: "span 2" }}
        />
      </Box>
    </>
  );
};
